/**
 * @fileoverview Document card for the Privacy Policy and Terms & Conditions pages.
 * @description The white card chrome shared by both pages: title, "last updated" date, the amber
 * "placeholder content" disclaimer banner, and the page's own numbered sections passed as
 * children. Pulled out of the two pages since they're otherwise identical except for that content.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface LegalDocCardProps {
  title: string
  updatedLabel: string
  disclaimer: React.ReactNode
  children: React.ReactNode
}

/**
 * @component
 * @param {LegalDocCardProps} props - The card's title/date/disclaimer copy plus its section
 * content.
 * @param {string} props.title - The document title (e.g. "Privacy Policy").
 * @param {string} props.updatedLabel - The "Last updated: ..." line shown below the title.
 * @param {React.ReactNode} props.disclaimer - The amber placeholder-content disclaimer, e.g. a
 * bold lead-in span followed by plain text.
 * @param {React.ReactNode} props.children - The document's own numbered sections.
 */
function LegalDocCard({ title, updatedLabel, disclaimer, children }: LegalDocCardProps) {
  return (
    <div className="rounded-2xl border border-divider bg-white p-8 shadow-card sm:p-11">
      <h1 className="mb-1.5 text-[28px] font-extrabold leading-tight text-ink">{title}</h1>
      <p className="mb-8 text-[13px] text-footer">{updatedLabel}</p>
      <div className="mb-8 rounded-[10px] border border-warning-border bg-warning-bg px-4 py-3 text-[12.5px] leading-[19px] text-warning-text">
        {disclaimer}
      </div>
      {children}
    </div>
  )
}

export default LegalDocCard
